import axios from "axios";
import { storageService } from "./storageService";
import { ROUTES } from "../constants/routes";
import { isAccessTokenExpired, refreshAccessToken } from "./tokenRefreshService";

const loginApiUrl = "/api/v1/accounts/login";
const refreshTokenApiUrl = "/api/v1/accounts/refresh-token";

const instance = axios.create({
    baseURL: window.__env.apiUrl,
    headers: {
        "Content-Type": "application/json",
    },
});

const logout = () => {
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    storageService.clearStorage();
    window.location.href = `${ROUTES.auth.login}?returnUrl=${returnUrl}`;
};

/** True if another tab already wrote a usable access token to storage. */
function hasFreshAccessTokenFromOtherTab(previousAccessToken?: string | null): boolean {
    const tokenData = storageService.getTokenData();
    if (!tokenData?.accessToken || isAccessTokenExpired()) {
        return false;
    }
    if (previousAccessToken && tokenData.accessToken === previousAccessToken) {
        return false;
    }
    return true;
}

instance.interceptors.request.use(
    (config) => {
        const token = storageService.getTokenData()?.accessToken;
        if (token) {
            config.headers["Authorization"] = 'Bearer ' + token;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

instance.interceptors.response.use(
    (res) => {
        return res;
    },
    async (err) => {
        const originalConfig = err.config;

        if (!err.response || err.response.status !== 401 || originalConfig.url === loginApiUrl) {
            return Promise.reject(err);
        }

        if (originalConfig.url === refreshTokenApiUrl) {
            // Another tab may have already rotated tokens while this refresh response arrived.
            if (hasFreshAccessTokenFromOtherTab()) {
                return Promise.reject(err);
            }
            logout();
            return Promise.reject(new Error(err.message));
        }

        if (originalConfig._retry) {
            return Promise.reject(err);
        }

        originalConfig._retry = true;

        const tokenBeforeRefresh = storageService.getTokenData()?.accessToken;
        const refreshToken = storageService.getTokenData()?.refreshToken;
        if (!refreshToken) {
            return Promise.reject(err);
        }

        try {
            await refreshAccessToken();
            return instance(originalConfig);
        } catch (refreshError) {
            if (hasFreshAccessTokenFromOtherTab(tokenBeforeRefresh)) {
                return instance(originalConfig);
            }
            logout();
            return Promise.reject(refreshError);
        }
    }
);

export default instance;
